import { Injectable } from '@angular/core'
import { LogService } from 'htv-lib'
import { PlatformService } from '../platforms/platform.service'
import { LocalApp, LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { SmartAppSet } from '../interfaces/smart-app-set'
import { BehaviorSubject, Subscription } from 'rxjs'
import { SmartApp } from '../interfaces/smart-app'
import { MediaService } from './media.service'
import { MonitoringService } from './monitoring.service'
import { StorageService } from './storage.service'
import { HTVConfig } from '../interfaces/htv-config'
import { StateDataService } from './state-data.service'

@Injectable({
  providedIn: 'root',
})
export class SmartAppService {
  isLoading$ = new BehaviorSubject<boolean>(true)

  private readonly localAppsKey = 'localApps'

  tvApps: TVApp[] = []
  localApps: LocalApp[] = []
  smartAppSet?: SmartAppSet

  refreshTimer: any
  readonly refreshTimerDelayInMs = 5000

  netflixAppId?: string
  netflixHotelId = ''

  applicationInstalledSubject = new BehaviorSubject<string[]>([])
  applicationInstalledSubscription?: Subscription

  constructor(
    private logService: LogService,
    private mediaService: MediaService,
    private monitoringService: MonitoringService,
    private platformService: PlatformService,
    private stateDataService: StateDataService,
    private storageService: StorageService
  ) {
    // do nothing
  }

  async init(smartAppSet?: SmartAppSet, htvConfig?: HTVConfig): Promise<void> {
    this.isLoading$.next(true)
    this.logService.debug('[SmartAppService] Initializing with Humecloud data')

    // Reset
    this.tvApps = []
    this.localApps = []
    this.smartAppSet = smartAppSet

    // Load Netflix Hotel ID
    if (htvConfig && htvConfig.netflix && htvConfig.netflix.hotelId) {
      this.netflixHotelId = htvConfig.netflix.hotelId
    } else {
      this.netflixHotelId = this.stateDataService.customerId ?? ''
    }

    this.registerApplicationInstalledListener()

    // Load saved localApps
    const savedLocalApps = this.storageService.getJson(this.localAppsKey)
    if (savedLocalApps) {
      this.localApps = savedLocalApps
    }

    // Launch with timeout to not block initialization procedure
    setTimeout(async () => {
      this.loadApps()
    }, 100)

    return Promise.resolve()
  }

  async loadApps(): Promise<void> {
    this.tvApps = await this.platformService.getAppList()
    this.localApps = this.generateLocalApps()
    this.storageService.saveJson(this.localAppsKey, this.localApps)
    this.isLoading$.next(false)
  }

  private generateLocalApps(): LocalApp[] {
    const localApps: LocalApp[] = []
    let netflixAppId = undefined

    if (this.smartAppSet) {
      this.smartAppSet.apps.forEach(smartApp => {
        const tvApp = this.searchTVApp(smartApp)

        let localApp: LocalApp
        if (tvApp) {
          localApp = {
            id: smartApp.id,
            available: true,
            iconUrl: this.mediaService.getMediaUrl(smartApp.iconMediaId) ?? tvApp.iconPath ?? '',
            smartApp: smartApp,
            tvApp: tvApp,
          }
          this.logService.info(
            `[SmartAppService] Loaded app ${localApp.smartApp.label} (${localApp.id}) with ${localApp.tvApp?.name} TV app`
          )
        } else {
          localApp = {
            id: smartApp.id,
            available: false,
            iconUrl: this.mediaService.getMediaUrl(smartApp.iconMediaId) ?? '',
            smartApp: smartApp,
            tvApp: undefined,
          }
          this.logService.error(
            `[SmartAppService] Could not find TV app for ${localApp.smartApp.label} (${localApp.id})`
          )
        }

        localApps.push(localApp)

        // Basic association of Netflix app for remote control Netflix button
        if (localApp.tvApp?.id.toLowerCase().includes('netflix')) {
          netflixAppId = localApp.id
        }
      })
    }

    this.netflixAppId = netflixAppId

    this.logService.info(`[SmartAppService] Loaded ${localApps.length} apps`)
    return localApps
  }

  isAppAvailable(appId: string): boolean {
    return this.localApps.some(it => it.id === appId && it.tvApp?.id)
  }

  launchApp(appId: string, launchMode: LocalAppLaunchMode): void {
    const localApp = this.localApps.find(it => it.id === appId)
    if (localApp && localApp.tvApp?.id) {
      if (localApp.id === this.netflixAppId) {
        this.platformService.startNetflixApp(localApp.tvApp.id, this.netflixHotelId, launchMode)
      } else {
        this.logService.info(`[SmartAppService] Starting app ${localApp.smartApp.label} (${localApp.tvApp.id})`)
        this.platformService.startApp(localApp.tvApp.id)
      }
    } else {
      this.logService.error(`[SmartAppService] Could not find app with id ${appId}`)
    }
  }

  launchNetflix(launchMode: LocalAppLaunchMode): void {
    if (this.netflixAppId) {
      this.launchApp(this.netflixAppId, launchMode)
    }
  }

  getAppList(): Promise<TVApp[]> {
    return Promise.resolve(this.tvApps)
  }

  private searchTVApp(smartApp: SmartApp): TVApp | undefined {
    const manufacturer = this.monitoringService.getSystemData().manufacturer

    // Retrieve corresponding appIds for TV model/manufacturer
    let appIds: string[] = []
    const smartAppId = smartApp.appIds.find(it => it.manufacturer === manufacturer)
    if (smartAppId) {
      appIds = smartAppId.ids
    }

    // Search installed TV apps for corresponding app id
    let tvApp: TVApp | undefined = undefined
    const smartAppTVApps = this.tvApps.filter(it => appIds.includes(it.id))
    if (smartAppTVApps.length > 0) {
      tvApp = smartAppTVApps[0]
    }

    return tvApp
  }

  registerApplicationInstalledListener(): void {
    if (this.applicationInstalledSubscription) {
      this.applicationInstalledSubscription?.unsubscribe()
    }
    this.applicationInstalledSubscription = this.applicationInstalledSubject.subscribe((appIds: string[]) => {
      this.logService.debug(`[SmartAppService] Application installed: ${appIds}`)
      if (appIds.length > 0) {
        this.loadApps()
      }
    })
    this.platformService.registerApplicationInstalledListener(this.applicationInstalledSubject)
  }
}
